import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { formatAppDate, formatAppDateFromKey, formatAppDateRange } from "../../utils/dateFormat";
import { formatPositionStatusLabel } from "../../utils/positionStatus";
import api from "../../services/axios";
import RecruiterSsoDropdown from "../../components/custom/RecruiterSsoDropdown";
import {
  Users, Globe, Calendar, ChevronDown, Building2, ThumbsUp, ThumbsDown, Clock,
  TrendingUp, CheckCircle2, Briefcase, X, ChevronLeft, ChevronRight, Search,
} from "lucide-react";

function tenantQuery(ids) {
  const list = ids instanceof Set ? Array.from(ids).filter(Boolean) : [];
  return list.length ? { tenantIds: list.join(",") } : {};
}

function toIsoDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const card = { background: "var(--ds-card)", border: "1px solid var(--ds-border)", borderRadius: 12, boxShadow: "var(--ds-shadow-md)" };
const card2 = { background: "var(--ds-card2)", border: "1px solid var(--ds-border)", borderRadius: 12 };
const inp = {
  background: "var(--ds-input-bg)", border: "1.5px solid var(--ds-border)",
  borderRadius: 8, color: "var(--ds-text)", padding: "8px 12px",
  fontSize: 13, outline: "none", width: "100%", fontFamily: "inherit",
};
const labelStyle = { fontSize: 12, fontWeight: 700, color: "var(--ds-text2)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 };

const COUNTRY_OPTIONS = [
  { id: "all", name: "All Countries" },
  { id: "india", name: "India" },
  { id: "usa", name: "United States" },
  { id: "uk", name: "United Kingdom" },
  { id: "uae", name: "UAE" },
  { id: "canada", name: "Canada" },
];

/* Client funnel — All Positions → Progress → Closed. Distinct teal palette vs JD blues/red. */
const FUNNEL_STAGE_META = [
  { key: "allPositions", label: "All Positions", color: "#0f766e" },
  { key: "progress", label: "Progress", color: "#14b8a6" },
  { key: "closed", label: "Closed", color: "#f59e0b" },
];

const POSITION_PAGE_SIZE = 8;

/** Round aging days: 23.1–23.5 → 23, 23.6–23.9 → 24 */
function roundAgingDays(v) {
  if (v == null || Number.isNaN(Number(v))) return null;
  const x = Number(v);
  const frac = x - Math.floor(x);
  return frac <= 0.5 ? Math.floor(x) : Math.ceil(x);
}

function fmtAgingDays(v) {
  const n = roundAgingDays(v);
  return n == null ? "—" : `${n}d`;
}

/** Days from createdAt to now (progress) or closedAt/updatedAt (closed). */
function positionAgingDays(pos, mode) {
  if (!pos?.createdAt) return null;
  const start = new Date(pos.createdAt).getTime();
  if (Number.isNaN(start)) return null;
  let end = Date.now();
  if (mode === "closed") {
    const closedRaw = pos.closedAt || pos.updatedAt;
    if (closedRaw) {
      const closed = new Date(closedRaw).getTime();
      if (!Number.isNaN(closed)) end = closed;
    }
  }
  const days = (end - start) / 86400000;
  return days < 0 ? null : days;
}

function toLocalDateKey(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function createdByLabel(pos) {
  const name = String(pos?.createdBy || "").trim();
  const email = String(pos?.createdByEmail || "").trim();
  return name || email || "—";
}

function matchesClientFilter(position, selectedClient) {
  if (!selectedClient || selectedClient === "all") return true;
  return String(position?.clientName || "").trim().toLowerCase() === String(selectedClient).trim().toLowerCase();
}

function matchesCountryFilter(position, selectedCountry) {
  if (!selectedCountry || selectedCountry === "all") return true;
  const country = String(position?.country || position?.Country || "").trim().toLowerCase();
  if (!country) return true;
  return country === selectedCountry || country.includes(selectedCountry);
}

function matchesDateRange(position, from, to) {
  const key = toLocalDateKey(position?.createdAt);
  if (!key) return true;
  if (from && key < from) return false;
  if (to && key > to) return false;
  return true;
}

function positionStatusKey(position) {
  const raw = (position?.status ?? position?.Status ?? position?.positionStatus ?? "Pending");
  const lower = String(raw).trim().toLowerCase();
  if (lower === "stacked") return "Stacked";
  if (lower === "close" || lower === "closed") return "Close";
  return "Pending";
}

function AllPositionsModal({ open, onClose, clientName, from, to, mode = "all", agingBucket = null, selectedTenantIds }) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusTab, setStatusTab] = useState("all");
  const [page, setPage] = useState(1);

  // Status filter pills removed for All Positions / Progress / Closed — lists are already scoped by mode.
  const tabs = useMemo(() => [], []);

  const title =
    mode === "aging" ? (agingBucket === "all" ? "Role Aging — All Open Roles" : `Role Aging — ${agingBucket} days`) :
    mode === "progress" ? "Progress" :
    mode === "closed" ? "Closed" :
    "All Positions";

  const subtitle =
    mode === "aging"
      ? `Open positions aged ${agingBucket === "all" ? "across all buckets" : agingBucket + " days"}${clientName ? ` for ${clientName}` : ""}.`
      : mode === "progress"
        ? `Active positions${clientName ? ` for ${clientName}` : ""} (Pending & Stacked) — includes who created each role.`
        : mode === "closed"
          ? `Closed positions${clientName ? ` for ${clientName}` : ""} — includes who created each role.`
          : `All positions${clientName ? ` for ${clientName}` : ""} (Progress & Closed) — includes who created each role.`;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setStatusTab("all");
    setPage(1);
    api.get("/analytics/positions-with-counts", { params: tenantQuery(selectedTenantIds) })
      .then((res) => {
        if (cancelled) return;
        setPositions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load positions.");
        setPositions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, clientName, from, to, mode, selectedTenantIds]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const scoped = useMemo(() => {
    let list = positions.filter((p) =>
      matchesClientFilter(p, clientName || "all")
      && matchesDateRange(p, from, to)
    );
    if (mode === "progress") {
      list = list.filter((p) => {
        const k = positionStatusKey(p);
        return k === "Pending" || k === "Stacked";
      });
    } else if (mode === "closed") {
      list = list.filter((p) => positionStatusKey(p) === "Close");
    } else if (mode === "aging") {
      list = list.filter((p) => {
        const k = positionStatusKey(p);
        if (k !== "Pending" && k !== "Stacked") return false;
        if (!agingBucket || agingBucket === "all") return true;
        const days = positionAgingDays(p, "progress");
        if (days == null) return false;
        const rounded = roundAgingDays(days);
        if (rounded == null) return false;
        if (agingBucket === "30+") return rounded >= 31;
        if (agingBucket === "0-7") return rounded >= 0 && rounded <= 7;
        if (agingBucket === "8-14") return rounded >= 8 && rounded <= 14;
        if (agingBucket === "15-30") return rounded >= 15 && rounded <= 30;
        return false;
      });
    }
    return list;
  }, [positions, clientName, from, to, mode, agingBucket]);

  const counts = useMemo(() => {
    const c = { all: scoped.length, Pending: 0, Stacked: 0, Close: 0 };
    scoped.forEach((p) => { c[positionStatusKey(p)] += 1; });
    return c;
  }, [scoped]);

  const filtered = useMemo(() => {
    if (statusTab === "all") return scoped;
    return scoped.filter((p) => positionStatusKey(p) === statusTab);
  }, [scoped, statusTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / POSITION_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = filtered.length === 0 ? 0 : (safePage - 1) * POSITION_PAGE_SIZE + 1;
  const end = Math.min(safePage * POSITION_PAGE_SIZE, filtered.length);
  const pageRows = filtered.slice((safePage - 1) * POSITION_PAGE_SIZE, safePage * POSITION_PAGE_SIZE);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed",
        top: 0, left: 0, right: 0, bottom: 0,
        width: "100vw", height: "100vh",
        zIndex: 12000,
        background: "rgba(15,23,42,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        paddingLeft: "calc(var(--app-sidebar-width, 0px) + 16px)",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "min(920px, calc(100vw - var(--app-sidebar-width, 0px) - 32px))",
          maxHeight: "90vh",
          overflow: "hidden",
          margin: "auto",
          background: "var(--ds-card)",
          borderRadius: 16,
          border: "1px solid var(--ds-border)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
          display: "flex",
          flexDirection: "column",
          fontFamily: "inherit",
        }}
      >
        {/* Header */}
        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid var(--ds-border)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(15,118,110,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Briefcase size={18} color="#0f766e" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 900, fontSize: 18, color: "var(--ds-text)" }}>{title}</div>
              <div style={{ fontSize: 12, color: "var(--ds-text2)", marginTop: 2 }}>
                {subtitle}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32, height: 32, borderRadius: 8, border: "1px solid var(--ds-border)",
              background: "var(--ds-card2)", color: "var(--ds-muted)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Status tabs — hidden on Closed (already scoped) */}
        {tabs.length > 0 && (
        <div style={{ padding: "12px 22px", display: "flex", flexWrap: "wrap", gap: 8, borderBottom: "1px solid var(--ds-border)" }}>
          {tabs.map((tab) => {
            const active = statusTab === tab.id;
            const count = counts[tab.id] ?? 0;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => { setStatusTab(tab.id); setPage(1); }}
                style={{
                  borderRadius: 20, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                  fontFamily: "inherit",
                  border: active ? "1px solid transparent" : "1px solid var(--ds-border)",
                  background: active ? "var(--ds-hover)" : "var(--ds-card)",
                  color: active ? "var(--ds-text)" : "var(--ds-text2)",
                }}
              >
                {tab.label} ({count})
              </button>
            );
          })}
        </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "0 8px" }}>
          {loading ? (
            <div style={{ padding: 48, textAlign: "center", color: "var(--ds-muted)", fontWeight: 600, fontSize: 13 }}>Loading positions…</div>
          ) : error ? (
            <div style={{ padding: 48, textAlign: "center", color: "#ef4444", fontWeight: 600, fontSize: 13 }}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center", color: "var(--ds-muted)", fontWeight: 600, fontSize: 13 }}>No positions match this filter.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--ds-border)" }}>
                  {["#", "Job Role", "Client Name", "Job Code", "Created By", "Days", "From", "Status"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left", padding: "12px 14px", fontSize: 11, fontWeight: 800,
                        color: "var(--ds-muted)", letterSpacing: "0.04em", textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((pos, idx) => {
                  const sno = (safePage - 1) * POSITION_PAGE_SIZE + idx + 1;
                  const statusLabel = positionStatusKey(pos);
                  const isClosed = statusLabel === "Close";
                  const agingMode = isClosed ? "closed" : "progress";
                  return (
                    <tr key={pos.positionId ?? `${pos.jobCode}-${idx}`} style={{ borderBottom: "1px solid var(--ds-border)" }}>
                      <td style={{ padding: "12px 14px", fontWeight: 800, color: "var(--ds-text)" }}>{sno}</td>
                      <td style={{ padding: "12px 14px", fontWeight: 700, color: "var(--ds-text)", maxWidth: 220 }}>
                        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={pos.positionName || ""}>
                          {pos.positionName || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", color: "var(--ds-text2)", fontWeight: 600 }}>{pos.clientName || "—"}</td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{
                          display: "inline-block", background: "rgba(37,99,235,0.08)", color: "#2563eb",
                          border: "1px solid rgba(37,99,235,0.2)", borderRadius: 6, padding: "2px 8px",
                          fontSize: 11, fontWeight: 700,
                        }}>
                          {pos.jobCode || "N/A"}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", color: "var(--ds-text2)", fontWeight: 600, maxWidth: 160 }}>
                        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={createdByLabel(pos)}>
                          {createdByLabel(pos)}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", fontWeight: 800, color: "#0d9488", whiteSpace: "nowrap" }}>
                        {fmtAgingDays(positionAgingDays(pos, agingMode))}
                      </td>
                      <td style={{ padding: "12px 14px", color: "var(--ds-text2)", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {formatAppDate(pos.createdAt) || "—"}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{
                          display: "inline-block",
                          borderRadius: 20,
                          padding: "3px 8px",
                          fontSize: 10,
                          fontWeight: 700,
                          background: statusLabel === "Close" ? "rgba(245,158,11,0.15)" : statusLabel === "Stacked" ? "rgba(20,184,166,0.15)" : "rgba(15,118,110,0.12)",
                          color: statusLabel === "Close" ? "#d97706" : statusLabel === "Stacked" ? "#0f766e" : "#0f766e",
                        }}>
                          {formatPositionStatusLabel(pos)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 22px", borderTop: "1px solid var(--ds-border)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        }}>
          <div style={{ fontSize: 12, color: "var(--ds-muted)", fontWeight: 600 }}>
            {filtered.length === 0 ? "Showing 0 of 0" : `Showing ${start}–${end} of ${filtered.length}`}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{
                width: 30, height: 30, borderRadius: 8, border: "1px solid var(--ds-border)",
                background: "var(--ds-card)", color: "var(--ds-text2)", cursor: safePage <= 1 ? "not-allowed" : "pointer",
                opacity: safePage <= 1 ? 0.45 : 1, display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
              Math.max(0, safePage - 3),
              Math.max(0, safePage - 3) + 5
            ).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                style={{
                  minWidth: 30, height: 30, borderRadius: 8, border: n === safePage ? "1px solid #0041C2" : "1px solid var(--ds-border)",
                  background: n === safePage ? "rgba(0,65,194,0.1)" : "var(--ds-card)",
                  color: n === safePage ? "#0041C2" : "var(--ds-text2)",
                  fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{
                width: 30, height: 30, borderRadius: 8, border: "1px solid var(--ds-border)",
                background: "var(--ds-card)", color: "var(--ds-text2)", cursor: safePage >= totalPages ? "not-allowed" : "pointer",
                opacity: safePage >= totalPages ? 0.45 : 1, display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
function DonutChart({ slices, size = 130, thickness = 20 }) {
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;
  const total = slices.reduce((s, d) => s + d.value, 0);
  let accumulated = 0;

  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      {total === 0 ? (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--ds-border)" strokeWidth={thickness} />
      ) : (
        slices.map((s, i) => {
          const pct = s.value / total;
          const dash = pct * circ;
          const offset = circ * 0.25 - accumulated;
          accumulated += dash;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
              style={{ transition: "stroke-dasharray 0.5s ease" }}
            />
          );
        })
      )}
    </svg>
  );
}

function HiringReachFunnel({ stages, onStageClick }) {
  const W = 1000;
  const H = 148;
  const n = Math.max(stages.length, 1);
  const segW = W / n;
  /* Gentle taper so every stage still has room for label + value */
  const minHeightRatio = 0.78;
  const bottomY = (x) => H - H * (1 - minHeightRatio) * (x / W);

  return (
    <div style={{ position: "relative", width: "100%", height: 148 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: 148, display: "block" }}
        role="img"
        aria-label="Recruitment funnel"
      >
        {stages.map((stage, i) => {
          const x0 = i * segW;
          const x1 = (i + 1) * segW;
          const y0b = bottomY(x0);
          const y1b = bottomY(x1);
          const points = `${x0},0 ${x1},0 ${x1},${y1b} ${x0},${y0b}`;
          return (
            <polygon
              key={stage.key || stage.label}
              points={points}
              fill={stage.color}
              stroke="rgba(255,255,255,0.35)"
              strokeWidth="1"
              style={{ transition: "fill 0.3s ease" }}
            />
          );
        })}
      </svg>
      {/* Text sits only inside each segment’s visible height so numbers are never clipped */}
      <div style={{ position: "absolute", inset: 0 }}>
        {stages.map((stage, i) => {
          const midX = (i + 0.5) * segW;
          const visibleH = bottomY(midX);
          const heightPct = (visibleH / H) * 100;
          const clickable = typeof onStageClick === "function" && stage.clickable;
          return (
            <div
              key={stage.key || stage.label}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={clickable ? `Open ${stage.label}` : undefined}
              onClick={clickable ? () => onStageClick(stage) : undefined}
              onKeyDown={clickable ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onStageClick(stage);
                }
              } : undefined}
              style={{
                position: "absolute",
                left: `${(i / n) * 100}%`,
                width: `${100 / n}%`,
                top: 0,
                height: `${heightPct}%`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "6px 6px",
                color: "#fff",
                textAlign: "center",
                boxSizing: "border-box",
                overflow: "hidden",
                cursor: clickable ? "pointer" : "default",
                pointerEvents: clickable ? "auto" : "none",
              }}
            >
              <div
                style={{
                  fontSize: n > 3 ? 10 : 11,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  marginBottom: 4,
                  textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                  whiteSpace: n > 3 ? "normal" : "nowrap",
                }}
              >
                {stage.label}
              </div>
              <div
                style={{
                  fontSize: n > 3 ? 20 : 24,
                  fontWeight: 900,
                  lineHeight: 1,
                  textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                }}
              >
                {stage.value}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConversionCard({ icon: Icon, fromLabel, toLabel, fromColor, toColor, fromVal, toVal }) {
  return (
    <div style={{ ...card2, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 180 }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", background: fromColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={16} color="#fff" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
          <span style={{ color: fromColor }}>{fromLabel}</span>
          <span style={{ color: "var(--ds-text2)" }}> → </span>
          <span style={{ color: toColor }}>{toLabel}</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 800 }}>
          <span style={{ color: fromColor }}>{fromVal}</span>
          <span style={{ color: "var(--ds-text2)" }}> → </span>
          <span style={{ color: toColor }}>{toVal}</span>
        </div>
      </div>
    </div>
  );
}

function SelectDropdown({ label, icon: Icon, value, options, open, onToggle, onSelect, accentColor, searchPlaceholder }) {
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const name = String(o.name || "").toLowerCase();
      const extra = String(o.jobCode || o.email || o.id || "").toLowerCase();
      return name.includes(q) || extra.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onToggle(false);
    };
    document.addEventListener("mousedown", handler);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", handler);
      clearTimeout(t);
    };
  }, [open, onToggle]);

  return (
    <div style={{ flex: "1 1 180px", minWidth: 160 }} ref={ref}>
      <div style={labelStyle}>
        {Icon && <Icon size={14} />}
        {label}
      </div>
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => onToggle(!open)}
          style={{
            ...inp,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            textAlign: "left",
            borderColor: value !== "all" ? accentColor : "var(--ds-border)",
            background: value !== "all" ? `${accentColor}12` : "var(--ds-input-bg)",
            padding: "8px 10px 8px 12px",
          }}
        >
          <span style={{ fontSize: 13, color: value !== "all" ? accentColor : "var(--ds-muted)", fontWeight: value !== "all" ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {selected?.name || "Select…"}
          </span>
          <ChevronDown size={14} style={{ color: "var(--ds-muted)", flexShrink: 0, transition: "transform 0.18s", transform: open ? "rotate(180deg)" : "none" }} />
        </button>
        {open && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 9999,
            background: "var(--ds-card)", border: "1.5px solid var(--ds-border)",
            borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", minWidth: "100%",
            overflow: "hidden",
          }}>
            <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--ds-border)", display: "flex", alignItems: "center", gap: 8 }}>
              <Search size={14} color="var(--ds-muted)" style={{ flexShrink: 0 }} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder || `Search ${label}…`}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 13,
                  color: "var(--ds-text)",
                  fontFamily: "inherit",
                  padding: "4px 0",
                }}
              />
              {query ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); setQuery(""); inputRef.current?.focus(); }}
                  style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--ds-muted)", padding: 0, display: "flex" }}
                >
                  <X size={14} />
                </button>
              ) : null}
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {filtered.length === 0 ? (
                <div style={{ padding: "12px 14px", fontSize: 12, color: "var(--ds-muted)", fontWeight: 600 }}>
                  No matches
                </div>
              ) : (
                filtered.map((opt) => {
                  const isActive = opt.id === value;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => { onSelect(opt.id); onToggle(false); }}
                      style={{
                        display: "block", width: "100%", padding: "9px 14px",
                        background: isActive ? `${accentColor}12` : "transparent",
                        border: "none", borderBottom: "1px solid var(--ds-border)",
                        cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                        fontSize: 12, fontWeight: isActive ? 700 : 500, color: isActive ? accentColor : "var(--ds-text)",
                      }}
                    >
                      {opt.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClientAnalyticsSection({ selectedTenantIds, tenantOptions = [], onTenantChange }) {
  const navigate = useNavigate();
  const defaultTo = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; }, []);
  const todayMaxDate = useMemo(() => toIsoDate(new Date()), []);

  const [from, setFrom] = useState(() => toIsoDate(defaultFrom));
  const [to, setTo] = useState(() => toIsoDate(defaultTo));
  const [selectedClient, setSelectedClient] = useState("all");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [clientOpen, setClientOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [positionsModalMode, setPositionsModalMode] = useState(null);
  const [agingBucket, setAgingBucket] = useState(null);

  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/analytics/positions-with-counts", { params: tenantQuery(selectedTenantIds) })
      .then((res) => {
        if (cancelled) return;
        setPositions(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load client analytics.");
        setPositions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedTenantIds]);

  const clientOptions = useMemo(() => {
    const names = [...new Set(
      positions.map((p) => String(p.clientName || "").trim()).filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));
    return [{ id: "all", name: "All Clients" }, ...names.map((n) => ({ id: n, name: n }))];
  }, [positions]);

  /* Drop invalid client selection if list changes */
  useEffect(() => {
    if (selectedClient === "all") return;
    const ok = clientOptions.some((c) => c.id === selectedClient);
    if (!ok) setSelectedClient("all");
  }, [clientOptions, selectedClient]);

  const filteredPositions = useMemo(() => {
    return positions.filter((p) =>
      matchesClientFilter(p, selectedClient)
      && matchesCountryFilter(p, selectedCountry)
      && matchesDateRange(p, from, to)
    );
  }, [positions, selectedClient, selectedCountry, from, to]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const params = { from, to, ...tenantQuery(selectedTenantIds) };
    if (filteredPositions.length > 0 && filteredPositions.length !== positions.length) {
      params.positionIds = filteredPositions.map((p) => p.positionId).filter(Boolean).join(",");
    } else if (filteredPositions.length === 0 && positions.length > 0) {
      setSummary(null);
      return () => { cancelled = true; controller.abort(); };
    }
    api.get("/analytics/summary", { params, signal: controller.signal })
      .then((res) => {
        if (!cancelled) setSummary(res.data || null);
      })
      .catch((e) => {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        if (!cancelled) setSummary(null);
      });
    return () => { cancelled = true; controller.abort(); };
  }, [from, to, filteredPositions, positions.length, selectedTenantIds]);

  const n = (v) => {
    const x = Number(v ?? 0);
    return Number.isFinite(x) ? x : 0;
  };

  const allPositions = filteredPositions.length;
  const progress = filteredPositions.filter((p) => {
    const s = positionStatusKey(p);
    return s === "Pending" || s === "Stacked";
  }).length;
  const closed = filteredPositions.filter((p) => positionStatusKey(p) === "Close").length;

  const whatsappSent = n(summary?.outreachRecipients);
  const interested = n(summary?.candidatesInterested);
  const notInterested = n(summary?.candidatesNotInterested);
  const noResponse = n(summary?.candidatesNoResponse);
  const stackRankingRuns = n(summary?.stackRankingRuns);
  const resumesAnalyzed = n(summary?.resumesAnalyzed);
  const resumesScoredAbove50 = n(summary?.resumesScoredAboveFiftyPercent);

  const overallResponseRate = whatsappSent > 0
    ? Math.min(100, ((interested + notInterested) / whatsappSent) * 100)
    : 0;

  const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);

  const funnelValues = { allPositions, progress, closed };

  const funnelStages = FUNNEL_STAGE_META.map((meta, i) => ({
    step: i + 1,
    key: meta.key,
    label: meta.label,
    value: funnelValues[meta.key] ?? 0,
    color: meta.color,
    clickable: meta.key === "allPositions" || meta.key === "progress" || meta.key === "closed",
  }));

  const handleFunnelStageClick = (stage) => {
    if (stage?.key === "allPositions") {
      setPositionsModalMode("all");
      return;
    }
    if (stage?.key === "progress") {
      setPositionsModalMode("progress");
      return;
    }
    if (stage?.key === "closed") {
      setPositionsModalMode("closed");
    }
  };

  const [allMeta, progressMeta, closedMeta] = FUNNEL_STAGE_META;

  const conversionCards = [
    {
      icon: TrendingUp,
      fromLabel: allMeta.label,
      toLabel: progressMeta.label,
      fromColor: allMeta.color,
      toColor: progressMeta.color,
      fromVal: allPositions,
      toVal: progress,
    },
    {
      icon: CheckCircle2,
      fromLabel: progressMeta.label,
      toLabel: closedMeta.label,
      fromColor: progressMeta.color,
      toColor: closedMeta.color,
      fromVal: progress,
      toVal: closed,
    },
  ];

  const activityBars = [
    { label: "Stack Ranking Runs", value: stackRankingRuns, max: Math.max(resumesAnalyzed, stackRankingRuns, 1), color: "#2f80ed" },
    { label: "Resumes Analyzed", value: resumesAnalyzed, max: Math.max(resumesAnalyzed, 1), color: "#2f80ed" },
    { label: "Resumes Scored >50%", value: resumesScoredAbove50, max: Math.max(resumesAnalyzed, 1), color: "#2f80ed" },
  ];

  const displayPeriod = formatAppDateRange(from, to);

  const selectedClientLabel = selectedClient === "all"
    ? null
    : (clientOptions.find((c) => c.id === selectedClient)?.name || selectedClient);

  const recentPositions = useMemo(() => {
    return [...filteredPositions]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 5);
  }, [filteredPositions]);

  /** Open-role aging buckets for Role Aging card (client/country filtered). */
  const roleAging = useMemo(() => {
    const buckets = [
      { key: "0-7", label: "0–7 days", min: 0, max: 7, color: "#0d9488", count: 0 },
      { key: "8-14", label: "8–14 days", min: 8, max: 14, color: "#f59e0b", count: 0 },
      { key: "15-30", label: "15–30 days", min: 15, max: 30, color: "#f97316", count: 0 },
      { key: "30+", label: "30+ days", min: 31, max: Infinity, color: "#ef4444", count: 0 },
    ];
    const openRoles = filteredPositions.filter((p) => {
      const s = positionStatusKey(p);
      return s === "Pending" || s === "Stacked";
    });
    let sumDays = 0;
    let agedCount = 0;
    openRoles.forEach((pos) => {
      const days = positionAgingDays(pos, "progress");
      if (days == null) return;
      const rounded = roundAgingDays(days);
      if (rounded == null) return;
      agedCount += 1;
      sumDays += rounded;
      const bucket = buckets.find((b) => rounded >= b.min && rounded <= b.max);
      if (bucket) bucket.count += 1;
    });
    const total = agedCount;
    const rows = buckets.map((b) => ({
      ...b,
      pct: total > 0 ? Math.round((b.count / total) * 100) : 0,
    }));
    const avgDays = total > 0 ? sumDays / total : 0;
    // Progress bar vs 60-day reference window (visual only)
    const avgBarPct = Math.min(100, (avgDays / 60) * 100);
    return { total, rows, avgDays, avgBarPct };
  }, [filteredPositions]);


  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Users size={22} color="#0041C2" />
        <h3 style={{ margin: 0, fontWeight: 900, color: "var(--ds-text)", fontSize: 22 }}>Client Analytics</h3>
      </div>
      <div style={{ color: "var(--ds-text2)", fontSize: 13, marginBottom: 20 }}>
        Shows all-time activity across every client by default — pick a client and/or a date range to scope the view.
      </div>

      {/* Filters */}
      <div style={{ ...card, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
          {tenantOptions.length > 0 && (
            <RecruiterSsoDropdown
              label="Tenant"
              icon={Building2}
              allLabel="All Tenants"
              itemNoun="tenants"
              emptyLabel="No tenants found"
              showDetail={false}
              accent="#7c3aed"
              recruiters={tenantOptions.map((t) => ({ email: String(t.id), name: t.name }))}
              selectedEmails={selectedTenantIds instanceof Set ? selectedTenantIds : new Set()}
              onChange={onTenantChange}
            />
          )}
          <SelectDropdown
            label="Client"
            icon={Building2}
            value={selectedClient}
            options={clientOptions}
            open={clientOpen}
            onToggle={setClientOpen}
            onSelect={setSelectedClient}
            accentColor="#0041C2"
          />
          <SelectDropdown
            label="Country"
            icon={Globe}
            value={selectedCountry}
            options={COUNTRY_OPTIONS}
            open={countryOpen}
            onToggle={setCountryOpen}
            onSelect={setSelectedCountry}
            accentColor="#059669"
          />
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <div style={labelStyle}><Calendar size={14} /> From</div>
            <input
              type="date"
              value={from}
              max={to || todayMaxDate}
              onChange={(e) => setFrom(e.target.value)}
              style={inp}
            />
          </div>
          <div style={{ flex: "1 1 140px", minWidth: 130 }}>
            <div style={labelStyle}><Calendar size={14} /> To</div>
            <input
              type="date"
              value={to}
              min={from}
              max={todayMaxDate}
              onChange={(e) => setTo(e.target.value)}
              style={inp}
            />
          </div>
        </div>
        {(selectedClient !== "all" || selectedCountry !== "all") && (
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "var(--ds-muted)", fontWeight: 600 }}>
              Live data for {displayPeriod}
            </span>
            {selectedClient !== "all" && (
              <span style={{ background: "rgba(0,65,194,0.1)", border: "1px solid rgba(0,65,194,0.25)", color: "#0041C2", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>
                {clientOptions.find((c) => c.id === selectedClient)?.name}
              </span>
            )}
            {selectedCountry !== "all" && (
              <span style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", color: "#059669", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600 }}>
                {COUNTRY_OPTIONS.find((c) => c.id === selectedCountry)?.name}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Client Analytics funnel */}
      <div style={{ ...card, padding: 20, marginBottom: 16 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 900, color: "var(--ds-text)", fontSize: 16 }}>Client Analytics</div>
          <div style={{ color: "var(--ds-text2)", fontSize: 12, marginTop: 4, maxWidth: 420 }}>
            Client-level pipeline across all positions, in-progress roles, and closed.
          </div>
        </div>

        <HiringReachFunnel stages={funnelStages} onStageClick={handleFunnelStageClick} />

        <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          {conversionCards.map((c) => (
            <ConversionCard key={`${c.fromLabel}-${c.toLabel}`} {...c} />
          ))}
        </div>

      </div>

      {/* Role Aging + Recent Positions */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16, alignItems: "stretch" }}>
        {/* Role Aging */}
        <div style={{ ...card, padding: 20, display: "flex", flexDirection: "column", height: "100%", boxSizing: "border-box" }}>
          <div style={{ fontWeight: 900, color: "var(--ds-text)", marginBottom: 16, fontSize: 15 }}>Role Aging</div>
          <div style={{ display: "flex", alignItems: "center", gap: 24, flex: 1, minHeight: 0 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <DonutChart
                size={130}
                thickness={20}
                slices={roleAging.rows.map((r) => ({ value: r.count, color: r.color }))}
              />
              <div onClick={() => { setAgingBucket("all"); setPositionsModalMode("aging"); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setAgingBucket("all"); setPositionsModalMode("aging"); } }} style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", borderRadius: "50%" }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: "var(--ds-text)", lineHeight: 1 }}>{roleAging.total}</div>
                <div style={{ fontSize: 9, color: "var(--ds-muted)", fontWeight: 600, marginTop: 2 }}>ROLES</div>
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 0 }}>
              {roleAging.rows.map((item, idx) => (
                <div
                  key={item.key}
                  onClick={() => { setAgingBucket(item.key); setPositionsModalMode("aging"); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setAgingBucket(item.key); setPositionsModalMode("aging"); } }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 0",
                    borderBottom: idx < roleAging.rows.length - 1 ? "1px solid var(--ds-border)" : "none",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "var(--ds-text2)", fontWeight: 600 }}>{item.label}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 800, color: "var(--ds-text)" }}>{item.count}</span>
                    <span style={{ fontSize: 11, color: "var(--ds-muted)", marginLeft: 6 }}>({item.pct}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ ...card2, padding: 14, marginTop: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ds-text2)" }}>Overall Avg Aging</span>
              <span style={{ fontSize: 16, fontWeight: 900, color: "var(--ds-text)" }}>{fmtAgingDays(roleAging.avgDays)}</span>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: "var(--ds-card)", overflow: "hidden" }}>
              <div style={{ width: `${roleAging.avgBarPct}%`, height: "100%", background: "linear-gradient(90deg,#0d9488,#14b8a6)", transition: "width 0.5s ease" }} />
            </div>
          </div>
        </div>

        {/* Recent Positions */}
        <div style={{ ...card, padding: 20, display: "flex", flexDirection: "column", height: "100%", boxSizing: "border-box" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexShrink: 0 }}>
            <div style={{ fontWeight: 900, color: "var(--ds-text)", fontSize: 15 }}>Recent Positions</div>
            <button type="button" onClick={() => setPositionsModalMode("all")} style={{ background: "none", border: "none", color: "#0041C2", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              View all
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 0, overflowY: "auto" }}>
            {recentPositions.length === 0 ? (
              <div style={{ padding: 16, textAlign: "center", color: "var(--ds-muted)", fontSize: 13, fontWeight: 600 }}>
                No positions match the current filters.
              </div>
            ) : recentPositions.map((pos) => {
              const st = positionStatusKey(pos);
              const style = st === "Stacked"
                ? { background: "rgba(22,163,74,0.12)", color: "#16a34a" }
                : st === "Close"
                  ? { background: "rgba(100,116,139,0.12)", color: "#475569" }
                  : { background: "rgba(37,99,235,0.1)", color: "#2563eb" };
              return (
                <div
                  key={pos.positionId || pos.positionName}
                  role="button"
                  tabIndex={0}
                  onClick={() => { if (pos.positionId != null) navigate(`/database/${pos.positionId}`); }}
                  onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && pos.positionId != null) navigate(`/database/${pos.positionId}`); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                    padding: "10px 12px", borderRadius: 10, border: "1px solid var(--ds-border)", background: "var(--ds-card2)",
                    cursor: pos.positionId != null ? "pointer" : "default",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(22,163,74,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Briefcase size={16} color="#16a34a" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "var(--ds-text)" }}>{pos.positionName || "—"}</div>
                      <div style={{ fontSize: 11, color: "var(--ds-muted)", marginTop: 2 }}>{pos.clientName || "—"}</div>
                      <div style={{ fontSize: 10, color: "var(--ds-muted)", marginTop: 2 }}>Created by {createdByLabel(pos)}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                    <span style={{ ...style, borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 700 }}>{formatPositionStatusLabel(pos)}</span>
                    <span style={{ fontSize: 12, color: "var(--ds-muted)", fontWeight: 600 }}>{pos.resumeCount ?? 0} candidates</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Funnel + Response */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Response Breakdown */}
        <div style={{ ...card, padding: 20 }}>
          <div style={{ fontWeight: 900, color: "var(--ds-text)", marginBottom: 16, fontSize: 15 }}>Response Breakdown</div>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <DonutChart
                size={130}
                thickness={20}
                slices={[
                  { value: interested, color: "#16a34a" },
                  { value: notInterested, color: "#ef4444" },
                  { value: noResponse, color: "var(--ds-border)" },
                ]}
              />
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: "var(--ds-text)", lineHeight: 1 }}>{whatsappSent}</div>
                <div style={{ fontSize: 9, color: "var(--ds-muted)", fontWeight: 600, marginTop: 2 }}>REPLIES</div>
              </div>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { label: "Interested", value: interested, color: "#16a34a", pct: pct(interested, whatsappSent), Icon: ThumbsUp },
                { label: "Not Interested", value: notInterested, color: "#ef4444", pct: pct(notInterested, whatsappSent), Icon: ThumbsDown },
                { label: "No Response", value: noResponse, color: "var(--ds-muted)", pct: pct(noResponse, whatsappSent), Icon: Clock },
              ].map((item) => (
                <div key={item.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <item.Icon size={14} color={item.color} />
                    <span style={{ fontSize: 12, color: "var(--ds-text2)", fontWeight: 600 }}>{item.label}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 800, color: "var(--ds-text)" }}>{item.value}</span>
                    <span style={{ fontSize: 11, color: "var(--ds-muted)", marginLeft: 6 }}>({item.pct}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ ...card2, padding: 14, marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ds-text2)" }}>Overall Response Rate</span>
              <span style={{ fontSize: 16, fontWeight: 900, color: "var(--ds-text)" }}>{overallResponseRate.toFixed(1)}%</span>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: "var(--ds-card)", overflow: "hidden" }}>
              <div style={{ width: `${Math.min(100, overallResponseRate)}%`, height: "100%", background: "linear-gradient(90deg,#0041C2,#1A5FD4)", transition: "width 0.5s ease" }} />
            </div>
          </div>
        </div>

        {/* Ranking & Scoring Activity */}
        <div style={{ ...card, padding: 20 }}>
          <div style={{ fontWeight: 900, color: "var(--ds-text)", marginBottom: 4, fontSize: 15 }}>Ranking &amp; Scoring Activity</div>
          <div style={{ color: "var(--ds-text2)", fontSize: 11, marginBottom: 18 }}>
            {selectedCountry !== "all"
              ? `Live metrics for ${COUNTRY_OPTIONS.find((c) => c.id === selectedCountry)?.name}`
              : "Activity summary across all countries"}
            {" · "}{formatAppDateFromKey(from)} → {formatAppDateFromKey(to)}
          </div>
          {activityBars.map((item) => (
            <div key={item.label} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: "var(--ds-text2)", fontWeight: 600 }}>{item.label}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: item.color }}>{item.value}</span>
              </div>
              <div style={{ height: 8, borderRadius: 999, background: "var(--ds-card2)", overflow: "hidden" }}>
                <div
                  style={{
                    width: `${Math.min(100, (item.value / item.max) * 100)}%`,
                    height: "100%",
                    background: `linear-gradient(90deg, ${item.color}99, ${item.color})`,
                    transition: "width 0.5s ease",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <AllPositionsModal
        open={positionsModalMode != null}
        mode={positionsModalMode || "all"}
        onClose={() => { setPositionsModalMode(null); setAgingBucket(null); }}
        clientName={selectedClientLabel}
        from={from}
        to={to}
        agingBucket={agingBucket}
        selectedTenantIds={selectedTenantIds}
      />

    </div>
  );
}
